import api from './api';
import {
  AD_CLICK_IDS_REVOKE_PENDING_KEY as CLICK_ID_REVOKE_PENDING_KEY,
  clearAdClickIds,
  getConsentedAdClickIds,
  isRevokePending,
  setRevokePending,
} from './adClickIds';
import { subscribeAnalyticsConsent } from './analyticsConsent';
import { getAccessToken } from '../utils/accessToken';

const ATTRIBUTION_STORAGE_KEY = 'utm_params';
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const APP_VERSION = process.env.REACT_APP_VERSION || 'unknown';

// Keys we read from query string and forward to the backend.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'];

const hasMeaningfulValue = (payload) =>
  payload != null && typeof payload === 'object' &&
  Object.values(payload).some((v) => v != null && String(v).trim() !== '');

const safeStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    return null;
  }
};

const readStoredEntry = () => {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Backwards compat: older builds wrote the bare payload (no envelope).
    if (parsed && typeof parsed === 'object' && parsed.payload) {
      if (typeof parsed.expiresAt === 'number' && parsed.expiresAt < Date.now()) {
        storage.removeItem(ATTRIBUTION_STORAGE_KEY);
        return null;
      }
      return parsed;
    }
    if (hasMeaningfulValue(parsed)) {
      // Migrate the legacy bare payload into the envelope shape with a fresh TTL.
      const migrated = {
        payload: parsed,
        capturedAt: Date.now(),
        expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
        captureMethod: parsed.captureMethod || 'legacy_session',
      };
      try {
        storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(migrated));
      } catch (_) {}
      return migrated;
    }
    return null;
  } catch (_) {
    return null;
  }
};

export const trackProductEvent = async (event, properties = {}) => {
  try {
    await api.post('/analytics/event', {
      event,
      properties: {
        platform: 'web',
        appVersion: APP_VERSION,
        ...properties,
      },
    });
  } catch (_) {
    // Best-effort analytics only.
  }
};

/**
 * Capture UTM/ref params from the current URL into localStorage. Safe to call
 * on every route mount — first-touch wins, expired entries are dropped, and a
 * URL with no UTM never overwrites a stored payload.
 *
 * @param {Object} [options]
 * @param {string} [options.captureMethod] - Tag identifying which call site
 *   captured this attribution (e.g. 'app_init', 'login_page'). Stored alongside
 *   the payload and forwarded to the backend on post.
 * @returns {boolean} true if a new payload was written; false otherwise.
 */
export const captureAttributionFromCurrentUrl = ({ captureMethod = 'app_init' } = {}) => {
  if (typeof window === 'undefined') return false;
  const storage = safeStorage();
  if (!storage) return false;

  try {
    const params = new URLSearchParams(window.location.search);
    const payload = UTM_KEYS.reduce((acc, key) => {
      acc[key] = params.get(key);
      return acc;
    }, {});
    if (!hasMeaningfulValue(payload)) return false;

    // First-touch wins. If we already have a non-expired entry, keep it.
    const existing = readStoredEntry();
    if (existing && hasMeaningfulValue(existing.payload)) return false;

    const envelope = {
      payload,
      capturedAt: Date.now(),
      expiresAt: Date.now() + ATTRIBUTION_TTL_MS,
      captureMethod,
    };
    storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(envelope));
    return true;
  } catch (_) {
    return false;
  }
};

const nonEmptyParam = (value) => value != null && String(value).trim() !== '';

/**
 * Search string for a same-origin hop that must keep attribution.
 * A URL that already carries a UTM or ref is returned unchanged. A URL that
 * lost its tags (an in-site link that dropped the query) gets the stored
 * first-touch values appended. First-touch storage itself is not rewritten.
 *
 * @param {string} [search]
 * @returns {string} `?a=b` or `''`
 */
export const searchWithStoredAttribution = (search = '') => {
  const raw = search == null ? '' : String(search);
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);
  const urlHasAttribution = UTM_KEYS.some((key) => nonEmptyParam(params.get(key)));
  if (urlHasAttribution) {
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  const entry = readStoredEntry();
  if (entry && hasMeaningfulValue(entry.payload)) {
    UTM_KEYS.forEach((key) => {
      const value = entry.payload[key];
      if (nonEmptyParam(value)) params.set(key, String(value));
    });
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

// ---------------------------------------------------------------------------
// Consent withdrawal reaches the server copy of the ad click ids (#2499)
// ---------------------------------------------------------------------------

const CLICK_ID_REVOKE_PATH = '/analytics/attribution/click-ids';

const flushPendingRevoke = async () => {
  if (!isRevokePending()) return;
  try {
    await api.delete(CLICK_ID_REVOKE_PATH);
    setRevokePending(false);
  } catch (_) {
    // Keep the flag; a later sign-in retries.
  }
};

/**
 * Ask the server to unset the stored click ids. Fire-and-forget: failures are
 * never surfaced, and the pending flag stays set until a 2xx. With an
 * in-memory access token it is a `keepalive` fetch, which survives the reload
 * that a withdrawal triggers when a pixel was injected
 * (reloadOnConsentWithdrawal.js). A non-2xx (e.g. 401 on an expired token)
 * or a network error falls back to api.delete, whose interceptor refreshes
 * the session and retries. With no token the flag alone makes the next
 * signed-in postAttributionFromStorage send it.
 */
export const revokeServerAdClickIds = () => {
  setRevokePending(true);
  const token = getAccessToken();
  if (!token || typeof fetch !== 'function') return;
  const fallback = () => flushPendingRevoke().catch(() => {});
  try {
    fetch(`${api.defaults.baseURL}${CLICK_ID_REVOKE_PATH}`, {
      method: 'DELETE',
      keepalive: true,
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        if (response && response.ok) setRevokePending(false);
        else fallback();
      })
      .catch(fallback);
  } catch (_) {
    fallback();
  }
};

// Every non-granted choice revokes, not only a transition seen in this tab:
// a grant made in another tab never reaches this tab's listeners, so this tab
// cannot know whether ids were posted. The DELETE is idempotent, and a
// signed-out visitor only gets the pending flag.
subscribeAnalyticsConsent((choice) => {
  if (choice !== 'granted') revokeServerAdClickIds();
});

/**
 * Send stored UTMs and, only with analytics consent granted, the ad click ids
 * (#2499) to the backend. Both are cleared after a successful post. A
 * withdrawal that could not reach the server yet is sent first.
 */
let attributionFlush = null;

const flushAttributionFromStorage = async () => {
  if (typeof window === 'undefined') return;
  const storage = safeStorage();
  if (!storage) return;

  await flushPendingRevoke();

  try {
    const entry = readStoredEntry();
    const hasUtm = Boolean(entry && hasMeaningfulValue(entry.payload));
    const clickIds = getConsentedAdClickIds();
    if (!hasUtm && !clickIds) return;

    await api.post('/analytics/attribution', {
      ...(hasUtm ? entry.payload : {}),
      ...(clickIds ? { clickIds } : {}),
      captureMethod: (hasUtm && entry.captureMethod) || 'client_api',
    });
    if (hasUtm) storage.removeItem(ATTRIBUTION_STORAGE_KEY);
    if (clickIds) clearAdClickIds();
  } catch (_) {
    // Best-effort only — leave the entry in storage so a later auth can retry.
  }
};

/**
 * Send stored attribution. Overlapping callers share one request so the
 * sign-up handoff can wait for the flush validateToken already started.
 */
export const postAttributionFromStorage = () => {
  if (!attributionFlush) {
    attributionFlush = flushAttributionFromStorage().finally(() => {
      attributionFlush = null;
    });
  }
  return attributionFlush;
};

// Exposed for tests.
export const __test = {
  ATTRIBUTION_STORAGE_KEY,
  ATTRIBUTION_TTL_MS,
  CLICK_ID_REVOKE_PENDING_KEY,
};
